// deps
import React, { ReactElement, useCallback, useMemo, useState } from "react";
import { Checkbox, CHECKBOX_ACCENT, InputFieldv4 } from "@swiggy-private/core-ui";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// interfaces
import { UpiPaymentMethod, VerifyVpaActions } from "@payment-ui/interfaces";

// constants
import { TEXTS, UPI_BOTTOM_ICON_HEIGHT, UPI_BOTTOM_ICON_WIDTH } from "@payment-ui/constants/Upi";

// widgets
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// helpers
import { isValidVpa } from "@payment-ui/helpers/Upi";
import { getIconCloudinaryUrl as getCloudinaryUrl } from "@payment-ui/helpers/Icon";

// test-ids
import { ADD_NEW_VPA_TEST_IDS } from "@swiggy-private/payment-test-ids";

import styles from "./AddNewVpa.v4.scss";

const UPI_ICONS_PATH = "PaymentLogos/paymentIcons/upiIcons";
export interface AddNewVpaProps {
    actions: VerifyVpaActions;
    verifyVpaInProgress: boolean;
    invalidVpa: boolean;
    methodData: UpiPaymentMethod;
}

export default function AddNewVpa(props: AddNewVpaProps): ReactElement {
    const [upiAddress, setUpiAddress] = useState("");
    const [saveVpa, setSaveVpa] = useState(true);
    const [invalidUpi, setInvalidUpi] = useState(false);
    const { verifyVpa } = props.actions;

    const onUpiChange = useCallback((value: string): void => {
        setUpiAddress(value?.trim());
        setInvalidUpi(false);
    }, []);

    const pay = useCallback(() => {
        if (!isValidVpa(upiAddress)) {
            setInvalidUpi(true);
            return;
        }

        verifyVpa(upiAddress, saveVpa, props.methodData);
    }, [props.methodData, saveVpa, upiAddress, verifyVpa]);

    const handleKeyEvent = useCallback(
        (event: React.KeyboardEvent): void => {
            if (event.keyCode === 13) {
                event.preventDefault();
                pay();
            }
        },
        [pay],
    );

    const onSaveVpaChange = useCallback((): void => {
        setSaveVpa(!saveVpa);
    }, [saveVpa]);

    const onClick = useCallback(
        (e: React.MouseEvent): void => {
            e.preventDefault();
            pay();
        },
        [pay],
    );

    const attrs = useMemo(
        () => ({
            disabled: props.verifyVpaInProgress,
            name: "upi-input",
            autoComplete: "off",
            "data-testid": ADD_NEW_VPA_TEST_IDS.UPI_ID_INPUT,
        }),
        [props.verifyVpaInProgress],
    );

    const checkboxAttrs = useMemo(
        () => ({
            "data-testid": ADD_NEW_VPA_TEST_IDS.SAVE_UPI_CHECKBOX,
        }),
        [],
    );
    const showInvalidUPIError = invalidUpi || props.invalidVpa;

    return (
        <>
            <div className={styles.content} data-testid={"add-new-vpa-container"}>
                <InputFieldv4
                    label={TEXTS.NEW_UPI_PLACEHOLDER_TEXT}
                    labelClassName={""}
                    className={styles.contentContainer}
                    value={upiAddress}
                    onChange={onUpiChange}
                    onDown={handleKeyEvent}
                    attrs={attrs}
                    error={invalidUpi || props.invalidVpa}
                    errorMessage={showInvalidUPIError ? "Invalid UPI" : undefined}
                />
                <div className={styles.contentSaveVpa}>
                    <Checkbox
                        accent={CHECKBOX_ACCENT.SWIGGY_ORANGE}
                        checked={saveVpa}
                        onChange={onSaveVpaChange}
                        name="save-vpa"
                        attrs={checkboxAttrs}
                    />
                    <span className={styles.contentSaveVpaText} data-testid={ADD_NEW_VPA_TEST_IDS.SAVE_UPI_TEXT}>
                        {props.methodData?.meta?.consentMsg ?? TEXTS.SECURELY_UPI}
                    </span>
                </div>
                <SuperButton
                    isDisabled={invalidUpi || upiAddress === ""}
                    onClick={onClick}
                    buttonType="primary"
                    variant="fullwidth"
                    buttonSize="large"
                    data-testid={ADD_NEW_VPA_TEST_IDS.VERIFY_AND_PAY_BUTTON}
                >
                    {TEXTS.VERIFY_AND_PAY}
                </SuperButton>

                <img className={styles.contentIcons} crossOrigin="anonymous" src={getCloudinaryUrl(UPI_ICONS_PATH)} />

                {props?.methodData?.meta?.bottomIconUrl && (
                    <Image
                        className={styles.contentBottomIcon}
                        cloudinaryId={props.methodData.meta?.bottomIconUrl}
                        cloudinaryHeight={UPI_BOTTOM_ICON_HEIGHT * 2}
                        cloudinaryWidth={UPI_BOTTOM_ICON_WIDTH * 2}
                        height={`${UPI_BOTTOM_ICON_HEIGHT}px`}
                        width={`${UPI_BOTTOM_ICON_WIDTH}px`}
                        data-testid={ADD_NEW_VPA_TEST_IDS.BOTTOM_ICON}
                    />
                )}
            </div>
        </>
    );
}
